"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Mail, Phone, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FormularioDeContacto() {
  return <section id="formulario-de-contacto" className="bg-primary py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <FormProvider className="grid md:grid-cols-2 gap-12 lg:gap-16" formId="contact_form_contact" sectionName="formulario-de-contacto" defaultValues={{
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: false
        },
        subject: {
          required: false
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Enviar Mensaje" successMessage="¡Gracias! Nos pondremos en contacto contigo pronto.">
          <ContactInfo />
          <ContactForm />
        </FormProvider>
      </div>
    </section>;
}
function ContactInfo() {
  const {
    ref: ref1,
    isVisible: isVisible1
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: ref2,
    isVisible: isVisible2
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: ref3,
    isVisible: isVisible3
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div className="space-y-8">
      <h2 className="font-display text-4xl lg:text-5xl text-foreground mb-12">
        Contáctanos
      </h2>
      
      <div ref={ref1} className={`flex items-start gap-4 transition-all duration-[var(--duration-medium)] ${isVisible1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <div className={`mt-1 ${isVisible1 ? "animate-pulse" : ""}`}>
          <Mail className="size-6 text-accent" strokeWidth={1} />
        </div>
        <div>
          <h3 className="font-medium text-foreground mb-1">Email</h3>
          <p className="text-muted-foreground">contacto@lumiereboutique.mx</p>
        </div>
      </div>

      <div ref={ref2} className={`flex items-start gap-4 transition-all duration-[var(--duration-medium)] delay-100 ${isVisible2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <div className={`mt-1 ${isVisible2 ? "animate-pulse" : ""}`}>
          <Phone className="size-6 text-accent" strokeWidth={1} />
        </div>
        <div>
          <h3 className="font-medium text-foreground mb-1">Teléfono</h3>
          <p className="text-muted-foreground">+52 55 6789 1011</p>
        </div>
      </div>

      <div ref={ref3} className={`flex items-start gap-4 transition-all duration-[var(--duration-medium)] delay-200 ${isVisible3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <div className={`mt-1 ${isVisible3 ? "animate-pulse" : ""}`}>
          <MapPin className="size-6 text-accent" strokeWidth={1} />
        </div>
        <div>
          <h3 className="font-medium text-foreground mb-1">Ubicación</h3>
          <p className="text-muted-foreground">Calle Ficticia 123, Ciudad de México, México</p>
        </div>
      </div>

      <div className="pt-8 border-t border-border">
        <p className="text-sm text-muted-foreground">Lunes a Sábado: 10am - 8pm</p>
      </div>
    </div>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4 animate-fade-in-up">
          <div className="size-16 mx-auto bg-accent/10 rounded-full flex items-center justify-center">
            <Mail className="size-8 text-accent" />
          </div>
          <p className="text-foreground text-lg font-medium">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="mt-4">
            Enviar Otro Mensaje
          </Button>
        </div>
      </div>;
  }
  return <div ref={formRef} className={`transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
      <Form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <FormField control={control} name="name" rules={{
          required: "El nombre es obligatorio",
          minLength: {
            value: 2,
            message: "Mínimo 2 caracteres"
          },
          maxLength: {
            value: 50,
            message: "Máximo 50 caracteres"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Nombre Completo</FormLabel>
                <FormControl>
                  <Input placeholder="Ejemplo: Ana Martínez" {...field} className="border-t-0 border-x-0 border-b border-border/40 rounded-none px-0 shadow-none focus-visible:border-accent focus-visible:ring-0 transition-colors duration-[var(--duration-medium)]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "El correo electrónico es obligatorio",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Correo electrónico inválido"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Correo Electrónico</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="ejemplo@correo.com" {...field} className="border-t-0 border-x-0 border-b border-border/40 rounded-none px-0 shadow-none focus-visible:border-accent focus-visible:ring-0 transition-colors duration-[var(--duration-medium)]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Teléfono (opcional)</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+52 55 1234 5678" {...field} className="border-t-0 border-x-0 border-b border-border/40 rounded-none px-0 shadow-none focus-visible:border-accent focus-visible:ring-0 transition-colors duration-[var(--duration-medium)]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="subject" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Asunto</FormLabel>
                <FormControl>
                  <Input placeholder="Ejemplo: Consulta sobre un producto" {...field} className="border-t-0 border-x-0 border-b border-border/40 rounded-none px-0 shadow-none focus-visible:border-accent focus-visible:ring-0 transition-colors duration-[var(--duration-medium)]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="message" rules={{
          required: "El mensaje es obligatorio",
          minLength: {
            value: 10,
            message: "Mínimo 10 caracteres"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground">Mensaje</FormLabel>
                <FormControl>
                  <Textarea placeholder="Cuéntanos cómo podemos ayudarte..." {...field} className="border-t-0 border-x-0 border-b border-border/40 rounded-none px-0 shadow-none focus-visible:border-accent focus-visible:ring-0 transition-colors duration-[var(--duration-medium)] min-h-24 resize-none" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg animate-fade-in-up">
              <p className="text-red-800 text-sm">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full bg-secondary text-foreground-secondary hover:bg-secondary-hover hover:scale-[1.02] active:scale-[0.98] transition-all duration-[var(--duration-fast)] shadow-soft h-12">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Enviando...
              </> : 'Enviar Mensaje'}
          </Button>
        </div>
      </Form>
    </div>;
}